import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    // Listen to clicks on the summary (user toggles)
    this.summary.addEventListener('click', this.handleClick, { signal });

    // Listen to keyboard events on host for Escape handling
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    // React to viewport changes
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Observe programmatic changes to details.open (so JS-set open true will trigger same behavior)
    this.#mutationObserver = new MutationObserver(this.#onDetailsMutated);
    this.#mutationObserver.observe(this.details, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();

    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser if disabled for the breakpoint
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Defer to microtask queue so the browser toggles <details>.open first.
    // After that, if the details is OPEN we close the others.
    queueMicrotask(() => {
      try {
        if (this.details.open) {
          // Current accordion was opened by this interaction
          this.#closeOthersOnOpen();
        }
      } catch (err) {
        // Fail silently - avoid breaking UI
        // console.error(err);
      }
    });
  };

  /**
   * MutationObserver callback: reacts to attribute changes on <details>.
   * We only care about cases where details.open becomes true (programmatic open).
   */
  /** @param {MutationRecord[]} mutationsList */
  #onDetailsMutated = (mutationsList) => {
    /** @type {MutationRecord[]} */
    const mutations = mutationsList;
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        // If details is now open, close others.
        try {
          if (this.details.open) {
            this.#closeOthersOnOpen();
          }
        } catch (err) {
          // swallow
        }
      }
    }
  };

  /**
   * Close other accordions in the same group (or all if no group specified).
   * This is only called when this.details.open === true (i.e., this accordion was opened).
   */
  #closeOthersOnOpen() {
    // IMPORTANT:
    // We only close other accordions within the *same local container* as this one.
    // Never query globally (document-wide), otherwise opening an accordion in one area
    // will close accordions in unrelated parts of the page (header, footer, other sections, etc.).

    const group = this.dataset.accordionGroup ?? null;

    // Find the nearest "accordion scope" container: the closest ancestor that contains
    // more than one accordion-custom. This keeps behavior localized without requiring
    // additional markup.
    /** @type {ParentNode | null} */
    let scope = this.parentElement;
    while (scope && scope !== document.documentElement) {
      try {
        const count = scope.querySelectorAll('accordion-custom').length;
        if (count > 1) break;
      } catch {
        // ignore and continue walking up
      }
      scope = scope.parentElement;
    }

    // Fallback: if we didn't find a better scope, constrain to the closest Shopify section
    // (still much safer than document-wide).
    if (!scope) {
      scope = this.closest('.shopify-section') ?? document;
    }

    const selector = group
      ? `accordion-custom[data-accordion-group="${group}"]`
      : 'accordion-custom';

    const accordions = Array.from(scope.querySelectorAll(selector));

    for (const el of accordions) {
      // Skip self
      if (el === this) continue;

      try {
        const details = el.querySelector('details');
        if (details instanceof HTMLDetailsElement && details.open) {
          // Close other open accordions
          details.open = false;
        }
      } catch (err) {
        // ignore per-item errors
      }
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
